/**
 * O domínio da tela de contratos (`TP-049`, T9) no front, em pt-BR.
 *
 * O contrato da API vem em inglês e é traduzido UMA vez, em `mapeadores.ts`,
 * logo depois do fetch — a tela não conhece o nome do fio (mesma disciplina
 * de `features/falhas-de-email/api/tipos.ts`).
 *
 * A seção `*Api` abaixo é a forma CRUA, escrita à mão: a geração de
 * `@anery/api-contracts` para este bloco (`/contratos`, `/fontes`) ainda não
 * rodou neste worktree — `node_modules` do pacote aponta para um worktree que
 * não existe mais (`DI13` do tech-plan). Os tipos espelham
 * `contracts/openapi/anery-api.v1.json` (schemas `ContractListItem`,
 * `ContractSourceListItem`, `ContractSourceDetail`, `ContractItem`,
 * `ContractComparisonItem`, `FonteEnviadaResponse`) linha a linha. Quando a
 * geração for consertada, trocar por `Schemas['ContractListItem']` etc.
 */

import type { SituacaoDaFonte, SituacaoDaFonteApi } from './situacaoDaFonte';

// ------------------------------------------------------------- forma da API

export type EntidadeDoContratoApi = 'HealthCare' | 'PlanOperator';

export type TipoDeFonteApi = 'ContractBase' | 'Amendment' | 'Readjustment' | 'ExternalTable' | 'Operational';

export type EstadoDaFonteApi =
  | 'Received'
  | 'TextExtracted'
  | 'Mapped'
  | 'RulesExtracted'
  | 'UnderReview'
  | 'Active'
  | 'Rejected';

export type MotivoDaDivergenciaApi = 'DifferentValue' | 'DifferentUnit' | 'OnlyInA' | 'OnlyInB';

export interface ContratoApi {
  readonly id: string;
  readonly name: string | null;
  readonly payerId: string;
  readonly entity: EntidadeDoContratoApi;
  readonly uf: string | null;
  readonly parentContractId: string | null;
  readonly linkedToIw: boolean;
}

export interface FonteResumoApi {
  readonly id: string;
  readonly type: TipoDeFonteApi;
  readonly state: EstadoDaFonteApi;
  readonly validFrom: string;
  readonly validTo: string | null;
  readonly createdAt: string;
}

export interface FonteDetalheApi {
  readonly id: string;
  readonly contractId: string | null;
  readonly type: TipoDeFonteApi;
  readonly state: EstadoDaFonteApi;
  readonly rejectionReason: string | null;
  readonly pages: number | null;
  readonly itemCount: number;
  readonly pendencyCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** `TP-067`: por que a fonte está onde está. Ausente em API anterior ao refino. */
  readonly situation?: SituacaoDaFonteApi | null;
}

export interface ItemApi {
  readonly id: string;
  readonly code: string | null;
  readonly description: string | null;
  readonly group: string | null;
  readonly billingUnit: string | null;
  readonly value: number;
  readonly page: number;
  readonly validFrom: string;
  readonly validTo: string | null;
  /** `TP-067`: a confiança do extrator na unidade e quando uma pessoa conferiu o item. */
  readonly unitConfidence?: 'High' | 'Medium' | 'Low';
  readonly reviewedAt?: string | null;
}

/** `GET /contratos/{id}/itens` (schema `ContractItemPage`): `page` começa em 1. */
export interface PaginaDeItensApi {
  readonly items: readonly ItemApi[] | null;
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

/** `GET /contratos/comparacao` (schema `ContractComparisonPage`): `page` começa em 1. */
export interface PaginaDeComparacaoApi {
  readonly items: readonly ItemDaComparacaoApi[] | null;
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ItemDaComparacaoApi {
  readonly code: string | null;
  readonly itemA: ItemApi | null;
  readonly itemB: ItemApi | null;
  readonly reason: MotivoDaDivergenciaApi;
}

export interface FonteEnviadaApi {
  readonly id: string;
}

/** `POST /contratos` (schema `CadastrarContratoRequest`). */
export interface CadastroDeContratoApi {
  readonly payerId: string;
  readonly entity: EntidadeDoContratoApi;
  readonly uf: string;
  readonly name: string;
}

// -------------------------------------------------------- domínio do front

export type Entidade = 'saude' | 'operadora_de_planos';

export type TipoDeFonte = 'contrato_base' | 'aditivo' | 'reajuste' | 'tabela_externa' | 'operacional';

/**
 * Ordem de exibição do seletor de tipo, no envio de PDF. Reajuste e tabela
 * externa ficam de fora: cada um tem envio próprio (`EnvioDeReajuste` e
 * `EnvioDeEdicao`), e um PDF enviado com esses tipos seguiria o
 * caminho de leitura de contrato.
 */
export const TIPOS_DE_FONTE: readonly TipoDeFonte[] = ['contrato_base', 'aditivo', 'operacional'];

/**
 * Tipos que continuam na lista, mas desabilitados: ainda não passaram pela leitura com um documento
 * real. O único aditivo enviado foi recusado (falta o contrato original da CASSI) e nenhum documento
 * operacional foi enviado; o código só trata os dois como o contrato-base. Cada tipo sai daqui
 * quando um documento real dele for lido de ponta a ponta.
 */
export const TIPOS_DE_FONTE_AINDA_NAO_VALIDADOS: ReadonlySet<TipoDeFonte> = new Set<TipoDeFonte>(['aditivo', 'operacional']);

export const ROTULOS_DE_TIPO_DE_FONTE: Record<TipoDeFonte, string> = {
  contrato_base: 'Contrato-base',
  aditivo: 'Aditivo',
  reajuste: 'Reajuste',
  tabela_externa: 'Tabela externa',
  operacional: 'Operacional',
};

/**
 * Os sete estados do vocabulário fechado (`BR-008`), embora a Fase 1
 * (`TP-049`) só movimente os três primeiros. Os demais aparecem aqui para a
 * tela nunca cair num estado "desconhecido" quando a Fase 2 chegar.
 */
export type EstadoDaFonte =
  | 'recebida'
  | 'texto_extraido'
  | 'mapeada'
  | 'regras_extraidas'
  | 'em_revisao'
  | 'ativa'
  | 'rejeitada';

export const ROTULOS_DE_ESTADO_DA_FONTE: Record<EstadoDaFonte, string> = {
  recebida: 'Recebida',
  texto_extraido: 'Texto extraído',
  mapeada: 'Mapeada',
  regras_extraidas: 'Regras extraídas',
  em_revisao: 'Em revisão',
  ativa: 'Ativa',
  rejeitada: 'Rejeitada',
};

export interface Contrato {
  readonly id: string;
  readonly nome: string;
  readonly uf: string | null;
  readonly entidade: Entidade;
  /** Tem contrato do IW ligado; sem isso não há tabela do IW para comparar. */
  readonly ligadoAoIw: boolean;
}

export const ROTULOS_DE_ENTIDADE: Record<Entidade, string> = {
  saude: 'Saúde',
  operadora_de_planos: 'Operadora de planos',
};

/** As 27 UFs, na ordem do seletor do cadastro (a API recusa qualquer outra sigla). */
export const UFS: readonly string[] = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
  'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

export interface CadastroDeContrato {
  readonly operadoraId: string;
  readonly entidade: Entidade;
  readonly uf: string;
  readonly nome: string;
}

/** "Bradesco Saúde — SP" — mesma operadora pode ter um contrato por UF (`D4`); sem a UF, dois
 * contratos-irmãos ficam indistinguíveis na tela. */
export function nomeDoContratoComUf(contrato: Contrato): string {
  return contrato.uf !== null ? `${contrato.nome} — ${contrato.uf}` : contrato.nome;
}

export interface FonteResumo {
  readonly id: string;
  readonly tipo: TipoDeFonte;
  readonly estado: EstadoDaFonte;
  readonly vigenciaInicio: string;
  readonly vigenciaFim: string | null;
  readonly criadaEm: string;
}

/** Uma pendência da fonte (`GET /fontes/{id}/pendencias`): código citado no texto que não virou item. */
export interface PendenciaDaFonteApi {
  readonly code: string;
  readonly page: number;
  readonly detail: string | null;
  readonly resolved: boolean;
}

export interface PendenciaDaFonte {
  readonly codigo: string;
  readonly pagina: number;
  readonly detalhe: string | null;
  readonly resolvida: boolean;
}

/**
 * O detalhe de uma fonte (`GET /fontes/{id}`). `totalDePendencias` é a
 * contagem; a lista com código e página vem de `GET /fontes/{id}/pendencias`.
 */
export interface FonteDetalhe {
  readonly id: string;
  readonly contratoId: string | null;
  readonly tipo: TipoDeFonte;
  readonly estado: EstadoDaFonte;
  readonly motivoDaRecusa: string | null;
  readonly paginas: number | null;
  readonly totalDeItens: number;
  readonly totalDePendencias: number;
  readonly criadaEm: string;
  readonly atualizadaEm: string;
  readonly situacao: SituacaoDaFonte | null;
}

export interface ItemDoContrato {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly grupo: string | null;
  readonly unidadeDeCobranca: string;
  readonly valor: number;
  readonly pagina: number;
  readonly vigenciaInicio: string;
  readonly vigenciaFim: string | null;
  readonly conferencia: ConferenciaDoItem;
}

/**
 * A conferência do item (`TP-067`, `D8`). A confiança vem do extrator; a revisão decide os itens de
 * confiança média e baixa. Mostrar só a confiança diria "mal lido" para um item que alguém já conferiu.
 */
export type ConferenciaDoItem = 'segura' | 'conferido' | 'nao_conferido';

export const CONFERENCIAS_DO_ITEM: readonly ConferenciaDoItem[] = ['segura', 'conferido', 'nao_conferido'];

export const ROTULOS_DE_CONFERENCIA: Record<ConferenciaDoItem, string> = {
  segura: 'Lido com segurança',
  conferido: 'Conferido na revisão',
  nao_conferido: 'Não conferido',
};

/** Uma página dos itens vigentes; `total` é o do filtro inteiro, e `pagina` começa em 1. */
export interface PaginaDeItens {
  readonly itens: readonly ItemDoContrato[];
  readonly total: number;
  readonly pagina: number;
  readonly tamanho: number;
}

export type MotivoDaDivergencia ='valor_diferente' | 'unidade_diferente' | 'so_no_primeiro' | 'so_no_segundo';

export const ROTULOS_DE_DIVERGENCIA: Record<MotivoDaDivergencia, string> = {
  valor_diferente: 'Valor diferente',
  unidade_diferente: 'Unidade diferente',
  so_no_primeiro: 'Só no primeiro contrato',
  so_no_segundo: 'Só no segundo contrato',
};

export interface ItemDaComparacao {
  readonly codigo: string;
  readonly itemA: ItemDoContrato | null;
  readonly itemB: ItemDoContrato | null;
  readonly motivo: MotivoDaDivergencia;
}

/** Uma página das divergências; `total` é o do filtro inteiro, e `pagina` começa em 1. */
export interface PaginaDeComparacao {
  readonly divergencias: readonly ItemDaComparacao[];
  readonly total: number;
  readonly pagina: number;
  readonly tamanho: number;
}

/** O que `enviarFonte` devolve — `criada` vem do STATUS http (200 × 201), nunca do corpo (`D6`). */
export interface ResultadoDoEnvio {
  readonly fonteId: string;
  readonly criada: boolean;
}
